import { createContext, useContext, useEffect, useMemo, useState } from "react";

// =====================================================
// Translation dictionaries
// =====================================================
const dict = {
  es: {
    code: "es",
    label: "ES",
    nav: {
      home: "Inicio",
      services: "Servicios",
      about: "Nosotros",
      testimonials: "Testimonios",
      contact: "Contacto",
      cta_consult: "Consulta Gratuita",
    },
    hero: {
      badge: "+2,500 Clientes Satisfechos · Houston, TX",
      title_pre: "Repara tu",
      title_em: "Crédito",
      title_post: "recupera tu tranquilidad.",
      subtitle:
        "En Master Credit Group eliminamos cuentas negativas, mejoramos tu puntaje y construimos contigo una base financiera sólida para lograr tus metas: una casa, un auto o tu propio negocio.",
      cta_primary: "Comenzar ahora",
      bullets: ["Atención en Español", "100% Confidencial", "Respuesta en 24h"],
      stat_label: "Promedio",
      stat_value: "+120 pts",
      stat_caption: "Mejora en puntaje crediticio en 90–180 días.",
      hero_alt: "Asesor financiero conversando con cliente",
    },
    services: {
      overline: "Nuestros Servicios",
      title_pre: "Soluciones completas para tu",
      title_em: "bienestar financiero.",
      subtitle:
        "Combinamos experiencia legal, estrategia crediticia y coaching financiero para darte resultados reales y duraderos.",
      cards: [
        {
          title: "Reparación de Crédito",
          desc: "Disputamos y eliminamos cuentas negativas, cobranzas y errores en tu reporte para subir tu puntaje.",
        },
        {
          title: "Asesoría Financiera",
          desc: "Estrategias personalizadas para recuperar el control de tus finanzas.",
        },
        {
          title: "Educación Financiera",
          desc: "Talleres y recursos para que nunca vuelvas a perder el rumbo.",
        },
        {
          title: "Preparación para Hipoteca",
          desc: "Te preparamos para calificar a tu primera casa en Estados Unidos.",
        },
      ],
      see_all: "Ver todos los servicios",
    },
    services_page: {
      overline: "Servicios",
      title: "Soluciones hechas a la medida de tu familia.",
      subtitle:
        "Cada servicio está diseñado para generar resultados visibles y sostenibles en tus finanzas.",
      items: [
        {
          title: "Reparación de Crédito",
          desc: "Auditamos tu reporte de las 3 burós (Experian, Equifax, TransUnion) y disputamos legalmente cuentas inexactas, obsoletas o no verificables.",
          features: ["Auditoría completa", "Disputas ilimitadas", "Seguimiento mensual", "Reporte de progreso"],
        },
        {
          title: "Asesoría Financiera",
          desc: "Plan financiero personalizado basado en tus metas: construir ahorro, invertir o prepararte para un préstamo grande.",
          features: ["Presupuesto personalizado", "Metas a corto/largo plazo", "Coaching 1:1", "Revisión trimestral"],
        },
        {
          title: "Educación Financiera",
          desc: "Talleres en español sobre cómo funciona el crédito, taxes, inversión y propiedad en Estados Unidos.",
          features: ["Clases grupales", "Materiales en español", "Certificado de participación", "Comunidad privada"],
        },
        {
          title: "Preparación para Hipoteca",
          desc: "Te preparamos financieramente para que califiques a tu primera casa en Estados Unidos.",
          features: ["Revisión de elegibilidad", "Optimización de puntaje", "Conexión con lenders", "Checklist de documentos"],
        },
        {
          title: "Monitoreo de Crédito",
          desc: "Alertas en tiempo real ante cualquier cambio, robo de identidad o actividad sospechosa.",
          features: ["Alertas 24/7", "Protección contra fraude", "Análisis mensual", "Simulador de puntaje"],
        },
      ],
    },
    about: {
      overline: "Sobre Nosotros",
      title_pre: "Una empresa construida para",
      title_em: "servir al mercado hispano.",
      body:
        "Master Credit Group nació con el propósito de acompañar a las familias hispanas en Estados Unidos a construir un historial crediticio sano. Creemos que con la información y la estrategia correcta, cualquier persona puede recuperar su poder financiero.",
      mission_title: "Nuestra Misión",
      mission_body:
        "Empoderar a nuestros clientes con herramientas y estrategias para alcanzar libertad financiera real.",
      vision_title: "Nuestra Visión",
      vision_body:
        "Ser la firma de asesoría crediticia de mayor confianza para la comunidad hispana en EE.UU.",
      stats: [
        { value: "10+", label: "Años de experiencia" },
        { value: "2,500+", label: "Clientes atendidos" },
        { value: "+120", label: "Puntos promedio" },
        { value: "95%", label: "Satisfacción" },
      ],
      cta_more: "Conoce más",
      cta_contact: "Hablemos",
    },
    about_page: {
      overline: "Sobre Nosotros",
      title: "Creemos en el poder transformador del crédito sano.",
      subtitle:
        "Una firma liderada por profesionales bilingües con pasión por servir a la comunidad hispana en Estados Unidos.",
      values_overline: "Nuestros valores",
      values_title: "Los principios que guían cada caso.",
      values: [
        { title: "Cercanía", desc: "Entendemos tu realidad porque somos parte de la misma comunidad." },
        { title: "Transparencia", desc: "Honestidad total en cada paso. Sin letras chicas ni promesas vacías." },
        { title: "Resultados", desc: "Trabajamos con metas medibles y reportes mensuales verificables." },
        { title: "Confianza", desc: "Cumplimos con FCRA, FDCPA y las regulaciones federales aplicables." },
      ],
    },
    team: {
      overline: "Nuestro Equipo",
      title_pre: "Las personas detrás de tu",
      title_em: "tranquilidad financiera.",
      subtitle:
        "Profesionales bilingües con experiencia comprobada, dedicados a transformar la realidad financiera de la comunidad hispana.",
      complete_label: "Equipo completo",
      members: [
        {
          name: "Nuestro Equipo",
          role: "Master Credit Group",
          bio: "Tres especialistas, una misión: devolverte el control de tu crédito y abrirte las puertas a la libertad financiera.",
          accent: "Equipo",
        },
        {
          name: "CEO · Master Credit",
          role: "Director Ejecutivo",
          bio: "Más de una década liderando la firma. Visión y experiencia para guiar cada caso con resultados reales.",
          accent: "Liderazgo",
        },
        {
          name: "Experto Senior",
          role: "Finanzas y Reparación de Crédito",
          bio: "Estrategia personalizada para cada cliente: disputas, consolidación y construcción de historial sólido.",
          accent: "Estrategia",
        },
        {
          name: "Experto Senior",
          role: "Finanzas y Reparación de Crédito",
          bio: "Especializado en eliminar cuentas negativas y preparar a las familias para grandes metas como hipoteca o auto.",
          accent: "Resultados",
        },
      ],
    },
    testimonials: {
      overline: "Testimonios",
      title_pre: "Historias reales que",
      title_em: "cambian vidas.",
      subtitle: "Miles de familias hispanas ya recuperaron su tranquilidad financiera con nosotros.",
      items: [
        { name: "María González", location: "Houston, TX", text: "En 4 meses mi puntaje subió 145 puntos. Pude comprar mi primera casa gracias a Master Credit. ¡Gracias por su profesionalismo!" },
        { name: "Carlos Ramírez", location: "Miami, FL", text: "Me explicaron todo en español y paso a paso. Eliminaron 6 cuentas negativas y ahora tengo mi crédito limpio." },
        { name: "Ana Martínez", location: "Los Angeles, CA", text: "Me ayudaron a consolidar mis deudas y hoy tengo un plan financiero claro. Recomendados al 100%." },
        { name: "Jorge Herrera", location: "Dallas, TX", text: "Pensé que mi crédito no tenía arreglo. En 6 meses ya califiqué para mi primer auto. Mil gracias." },
        { name: "Laura Díaz", location: "Orlando, FL", text: "El equipo es súper atento. Me sentí acompañada en todo momento. Ya compartí sus servicios con mi familia." },
      ],
    },
    testimonials_page: {
      overline: "Testimonios",
      title: "Resultados reales de familias como la tuya.",
      subtitle: "Historias de clientes que recuperaron su tranquilidad y lograron sus metas financieras.",
      stories: [
        { name: "María González", location: "Houston, TX", text: "Llegué con un puntaje de 520 y múltiples cuentas en cobranza. En 4 meses mi puntaje subió 145 puntos. Pude comprar mi primera casa. Estoy eternamente agradecida con todo el equipo de Master Credit.", result: "+145 pts" },
        { name: "Carlos Ramírez", location: "Miami, FL", text: "Me explicaron todo en español y paso a paso. Eliminaron 6 cuentas negativas y ahora tengo mi crédito limpio. Hoy califiqué para mi primer préstamo de negocio.", result: "6 cuentas eliminadas" },
        { name: "Ana Martínez", location: "Los Angeles, CA", text: "Me ayudaron a consolidar 9 deudas en un solo pago mensual más bajo. Por primera vez siento paz financiera.", result: "9 deudas consolidadas" },
        { name: "Jorge Herrera", location: "Dallas, TX", text: "Pensé que mi crédito no tenía arreglo después de mi divorcio. En 6 meses ya califiqué para mi primer auto sin cosigner.", result: "Préstamo auto aprobado" },
        { name: "Laura Díaz", location: "Orlando, FL", text: "El equipo es súper atento. Me sentí acompañada en todo momento. Ya compartí sus servicios con toda mi familia.", result: "+98 pts" },
        { name: "Roberto Sánchez", location: "Atlanta, GA", text: "Después de un robo de identidad quedé con mi crédito destruido. Master Credit no solo lo reparó sino que me enseñó a protegerlo.", result: "Identidad restaurada" },
      ],
    },
    contact: {
      overline: "Contáctanos",
      title_pre: "Tu consulta es",
      title_em: "100% gratuita.",
      subtitle: "Cuéntanos tu situación y un asesor se pondrá en contacto contigo en menos de 24 horas.",
      label_phone: "Teléfono",
      label_email: "Email",
      label_location: "Ubicación",
      form: {
        name: "Nombre",
        name_ph: "Tu nombre completo",
        email: "Email",
        email_ph: "tu@email.com",
        phone: "Teléfono",
        phone_ph: "(000) 000-0000",
        service: "Servicio de interés",
        service_ph: "Selecciona uno",
        services: [
          "Reparación de Crédito",
          "Asesoría Financiera",
          "Educación Financiera",
          "Preparación para Hipoteca",
          "Otro",
        ],
        message: "Mensaje",
        message_ph: "Cuéntanos brevemente tu situación...",
        submit: "Enviar mensaje",
        submitting: "Enviando...",
        disclaimer: "Al enviar, aceptas ser contactado por un asesor de Master Credit Group.",
        ok_toast: "¡Gracias! Nos pondremos en contacto muy pronto.",
        err_toast: "Hubo un error al enviar. Intenta de nuevo.",
        required_toast: "Por favor completa los campos requeridos.",
      },
    },
    contact_page: {
      overline: "Contacto",
      title: "Hablemos de tu futuro financiero.",
      subtitle: "Agenda una consulta gratuita. Un asesor bilingüe te responderá en menos de 24 horas.",
    },
    footer: {
      tagline: "Reparación de crédito y asesoría financiera profesional en Estados Unidos. Te ayudamos a construir un futuro financiero sólido, paso a paso.",
      nav_overline: "Navegación",
      contact_overline: "Contacto",
      copyright: "Todos los derechos reservados.",
      slogan: "Construyendo futuros financieros sólidos.",
    },
    whatsapp: {
      label: "Chatea por WhatsApp",
      message: "Hola, me interesa una consulta gratuita con Master Credit Group.",
    },
  },

  en: {
    code: "en",
    label: "EN",
    nav: {
      home: "Home",
      services: "Services",
      about: "About",
      testimonials: "Testimonials",
      contact: "Contact",
      cta_consult: "Free Consultation",
    },
    hero: {
      badge: "+2,500 Happy Clients · Houston, TX",
      title_pre: "Rebuild your",
      title_em: "Credit",
      title_post: "and reclaim your peace of mind.",
      subtitle:
        "At Master Credit Group we remove negative accounts, raise your score and help you build a solid financial foundation to reach your goals: a home, a car, or your own business.",
      cta_primary: "Get started",
      bullets: ["Bilingual support", "100% confidential", "Reply within 24h"],
      stat_label: "Average",
      stat_value: "+120 pts",
      stat_caption: "Credit score improvement in 90–180 days.",
      hero_alt: "Financial advisor talking with a client",
    },
    services: {
      overline: "Our Services",
      title_pre: "Complete solutions for your",
      title_em: "financial wellbeing.",
      subtitle:
        "We combine legal expertise, credit strategy and financial coaching to deliver real, lasting results.",
      cards: [
        {
          title: "Credit Repair",
          desc: "We dispute and remove negative accounts, collections and errors on your report to lift your score.",
        },
        {
          title: "Financial Advisory",
          desc: "Personalized strategies to take back control of your finances.",
        },
        {
          title: "Financial Education",
          desc: "Workshops and resources so you never lose track again.",
        },
        {
          title: "Mortgage Readiness",
          desc: "We get you ready to qualify for your first home in the United States.",
        },
      ],
      see_all: "See all services",
    },
    services_page: {
      overline: "Services",
      title: "Solutions tailored to your family.",
      subtitle: "Every service is designed to deliver visible, sustainable results for your finances.",
      items: [
        {
          title: "Credit Repair",
          desc: "We audit your report from the 3 bureaus (Experian, Equifax, TransUnion) and legally dispute inaccurate, outdated or unverifiable accounts.",
          features: ["Full audit", "Unlimited disputes", "Monthly follow-up", "Progress reports"],
        },
        {
          title: "Financial Advisory",
          desc: "Personalized financial plan based on your goals: build savings, invest, or prepare for a major loan.",
          features: ["Custom budget", "Short/long-term goals", "1:1 coaching", "Quarterly reviews"],
        },
        {
          title: "Financial Education",
          desc: "Bilingual workshops on how credit, taxes, investing and homeownership work in the U.S.",
          features: ["Group classes", "Bilingual materials", "Certificate of completion", "Private community"],
        },
        {
          title: "Mortgage Readiness",
          desc: "We prepare you financially to qualify for your first home in the United States.",
          features: ["Eligibility review", "Score optimization", "Lender connections", "Document checklist"],
        },
        {
          title: "Credit Monitoring",
          desc: "Real-time alerts for any change, identity theft or suspicious activity.",
          features: ["24/7 alerts", "Fraud protection", "Monthly analysis", "Score simulator"],
        },
      ],
    },
    about: {
      overline: "About Us",
      title_pre: "A company built to",
      title_em: "serve the Hispanic community.",
      body:
        "Master Credit Group was created to walk Hispanic families in the United States through the journey of building healthy credit. We believe that with the right information and strategy, anyone can reclaim their financial power.",
      mission_title: "Our Mission",
      mission_body: "Empower our clients with tools and strategies to reach real financial freedom.",
      vision_title: "Our Vision",
      vision_body: "Be the most trusted credit advisory firm for the Hispanic community in the U.S.",
      stats: [
        { value: "10+", label: "Years of experience" },
        { value: "2,500+", label: "Clients served" },
        { value: "+120", label: "Average points" },
        { value: "95%", label: "Satisfaction" },
      ],
      cta_more: "Learn more",
      cta_contact: "Let's talk",
    },
    about_page: {
      overline: "About Us",
      title: "We believe in the transformative power of healthy credit.",
      subtitle:
        "A firm led by bilingual professionals passionate about serving the Hispanic community in the United States.",
      values_overline: "Our values",
      values_title: "The principles that guide every case.",
      values: [
        { title: "Closeness", desc: "We understand your reality because we are part of the same community." },
        { title: "Transparency", desc: "Total honesty at every step. No fine print, no empty promises." },
        { title: "Results", desc: "We work with measurable goals and verifiable monthly reports." },
        { title: "Trust", desc: "We comply with FCRA, FDCPA and applicable federal regulations." },
      ],
    },
    team: {
      overline: "Our Team",
      title_pre: "The people behind your",
      title_em: "financial peace of mind.",
      subtitle:
        "Bilingual professionals with proven experience, dedicated to transforming the financial reality of the Hispanic community.",
      complete_label: "Full team",
      members: [
        {
          name: "Our Team",
          role: "Master Credit Group",
          bio: "Three specialists, one mission: give you back control of your credit and open the door to financial freedom.",
          accent: "Team",
        },
        {
          name: "CEO · Master Credit",
          role: "Chief Executive Officer",
          bio: "Over a decade leading the firm. Vision and experience to guide every case to real results.",
          accent: "Leadership",
        },
        {
          name: "Senior Expert",
          role: "Finance & Credit Repair",
          bio: "Tailored strategy for every client: disputes, consolidation and building solid history.",
          accent: "Strategy",
        },
        {
          name: "Senior Expert",
          role: "Finance & Credit Repair",
          bio: "Specialized in removing negative accounts and preparing families for big goals like a mortgage or car.",
          accent: "Results",
        },
      ],
    },
    testimonials: {
      overline: "Testimonials",
      title_pre: "Real stories that",
      title_em: "change lives.",
      subtitle: "Thousands of Hispanic families have already reclaimed their financial peace of mind with us.",
      items: [
        { name: "María González", location: "Houston, TX", text: "In 4 months my score went up 145 points. I was able to buy my first home thanks to Master Credit. Thank you for your professionalism!" },
        { name: "Carlos Ramírez", location: "Miami, FL", text: "They explained everything step by step. They removed 6 negative accounts and now my credit is clean." },
        { name: "Ana Martínez", location: "Los Angeles, CA", text: "They helped me consolidate my debts and today I have a clear financial plan. 100% recommended." },
        { name: "Jorge Herrera", location: "Dallas, TX", text: "I thought my credit was beyond repair. In 6 months I qualified for my first car. Thank you so much." },
        { name: "Laura Díaz", location: "Orlando, FL", text: "The team is super attentive. I felt accompanied the whole way. I already shared their services with my family." },
      ],
    },
    testimonials_page: {
      overline: "Testimonials",
      title: "Real results from families like yours.",
      subtitle: "Stories of clients who reclaimed their peace of mind and reached their financial goals.",
      stories: [
        { name: "María González", location: "Houston, TX", text: "I started with a 520 score and multiple collections. In 4 months my score went up 145 points. I bought my first home. Eternally grateful to the entire Master Credit team.", result: "+145 pts" },
        { name: "Carlos Ramírez", location: "Miami, FL", text: "They explained everything step by step. Removed 6 negative accounts and now my credit is clean. Today I qualified for my first business loan.", result: "6 accounts removed" },
        { name: "Ana Martínez", location: "Los Angeles, CA", text: "They helped me consolidate 9 debts into a single lower monthly payment. For the first time I feel financial peace.", result: "9 debts consolidated" },
        { name: "Jorge Herrera", location: "Dallas, TX", text: "I thought my credit was beyond repair after my divorce. In 6 months I qualified for my first car without a cosigner.", result: "Auto loan approved" },
        { name: "Laura Díaz", location: "Orlando, FL", text: "The team is super attentive. I felt accompanied the whole way. I already shared their services with my whole family.", result: "+98 pts" },
        { name: "Roberto Sánchez", location: "Atlanta, GA", text: "After identity theft my credit was destroyed. Master Credit not only repaired it but taught me how to protect it.", result: "Identity restored" },
      ],
    },
    contact: {
      overline: "Contact Us",
      title_pre: "Your consultation is",
      title_em: "100% free.",
      subtitle: "Tell us your situation and an advisor will contact you in less than 24 hours.",
      label_phone: "Phone",
      label_email: "Email",
      label_location: "Location",
      form: {
        name: "Name",
        name_ph: "Your full name",
        email: "Email",
        email_ph: "you@email.com",
        phone: "Phone",
        phone_ph: "(000) 000-0000",
        service: "Service of interest",
        service_ph: "Select one",
        services: [
          "Credit Repair",
          "Financial Advisory",
          "Financial Education",
          "Mortgage Readiness",
          "Other",
        ],
        message: "Message",
        message_ph: "Briefly tell us your situation...",
        submit: "Send message",
        submitting: "Sending...",
        disclaimer: "By submitting, you agree to be contacted by a Master Credit Group advisor.",
        ok_toast: "Thanks! We'll contact you very soon.",
        err_toast: "There was an error. Please try again.",
        required_toast: "Please fill in the required fields.",
      },
    },
    contact_page: {
      overline: "Contact",
      title: "Let's talk about your financial future.",
      subtitle: "Schedule a free consultation. A bilingual advisor will reply within 24 hours.",
    },
    footer: {
      tagline:
        "Professional credit repair and financial advisory in the United States. We help you build a solid financial future, step by step.",
      nav_overline: "Navigation",
      contact_overline: "Contact",
      copyright: "All rights reserved.",
      slogan: "Building solid financial futures.",
    },
    whatsapp: {
      label: "Chat on WhatsApp",
      message: "Hi, I'm interested in a free consultation with Master Credit Group.",
    },
  },
};

// =====================================================
// Context + hook
// =====================================================
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "es";
    const saved = localStorage.getItem("mc_lang");
    return saved === "en" || saved === "es" ? saved : "es";
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      localStorage.setItem("mc_lang", l);
      document.documentElement.lang = l;
    } catch {}
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      tr: dict[lang],
      toggle: () => setLang(lang === "es" ? "en" : "es"),
    }),
    [lang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
